import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import AddIcon from "@mui/icons-material/AddOutlined";
import { DataTable, type Column } from "@/components/data/data-table";
import { MonoText } from "@/components/display/mono";
import { StatusChip } from "@/components/display/status-chip";
import { EmptyState } from "@/components/feedback/states";
import { QueryEndpointDialog } from "@/components/endpoints/query-endpoint-dialog";
import { type QueryEndpoint, useQueryEndpoints } from "@/api/endpoints";

export const Route = createFileRoute("/_app/endpoints/query/")({
  component: QueryList,
});

function QueryList() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQueryEndpoints();
  const [creating, setCreating] = useState(false);

  const columns: Column<QueryEndpoint>[] = [
    { id: "name", header: "Name", cell: (r) => r.name, sortValue: (r) => r.name },
    { id: "slug", header: "Slug", cell: (r) => <MonoText>{r.slug}</MonoText>, nowrap: true },
    { id: "connector", header: "Connector", cell: (r) => r.connectorName ?? r.connectorId ?? "—" },
    {
      id: "enabled",
      header: "Status",
      cell: (r) => <StatusChip status={r.enabled === false ? "DISABLED" : "ACTIVE"} />,
    },
  ];

  return (
    <Card>
      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        error={error}
        onRetry={() => refetch()}
        rowKey={(r) => r.id}
        search={(r) => `${r.name ?? ""} ${r.slug ?? ""}`}
        searchPlaceholder="Search endpoints…"
        toolbar={
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
          >
            New endpoint
          </Button>
        }
        onRowClick={(r) => navigate({ to: "/endpoints/query/$id", params: { id: r.id } })}
        empty={
          <EmptyState
            title="No query endpoints"
            description="Expose a connector's data as a read-only GET API."
          />
        }
      />
      <QueryEndpointDialog open={creating} onClose={() => setCreating(false)} />
    </Card>
  );
}
